import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, ArrowUp, ArrowDown, Warning, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import ExportButtons from "@/components/ExportButtons";
import ImportDialog from "@/components/ImportDialog";
import { exportToExcel, exportToPDF } from "@/lib/export";

const KATEGORI = ["pakan", "probiotik", "obat", "peralatan", "lainnya"];
const STOK_COLS = [
  { header: "Nama", accessor: (r) => r.nama },
  { header: "Kategori", accessor: (r) => r.kategori },
  { header: "Stok", accessor: (r) => r.stok },
  { header: "Satuan", accessor: (r) => r.satuan },
  { header: "Harga Satuan", accessor: (r) => r.harga_satuan },
  { header: "Nilai", accessor: (r) => r.stok * r.harga_satuan },
];
const TX_COLS = [
  { header: "Tanggal", accessor: (r) => r.tanggal },
  { header: "Item", accessor: (r) => r.item_nama },
  { header: "Tipe", accessor: (r) => r.tipe },
  { header: "Jumlah", accessor: (r) => r.jumlah },
  { header: "Harga", accessor: (r) => r.harga_total },
  { header: "Keterangan", accessor: (r) => r.keterangan || "-" },
];

export default function GudangPage() {
  const [items, setItems] = useState([]);
  const [transaksi, setTransaksi] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalTx, setTotalTx] = useState(0);
  const [pageItems, setPageItems] = useState(1);
  const [pageTx, setPageTx] = useState(1);
  const [searchItems, setSearchItems] = useState("");
  const [searchTx, setSearchTx] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogItem, setDialogItem] = useState(false);
  const [dialogTx, setDialogTx] = useState(false);
  const [formItem, setFormItem] = useState({ nama: "", kategori: "pakan", satuan: "kg", stok: 0, harga_satuan: 0, stok_minimum: 0 });
  const [formTx, setFormTx] = useState({ item_id: "", tipe: "masuk", jumlah: 0, harga_total: 0, keterangan: "", tanggal: "" });

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get(`/gudang?page=${pageItems}&limit=15&search=${searchItems}`);
      setItems(data.data);
      setTotalItems(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [pageItems, searchItems]);

  const loadTx = useCallback(async () => {
    try {
      const { data } = await api.get(`/gudang/transaksi?page=${pageTx}&limit=15&search=${searchTx}`);
      setTransaksi(data.data);
      setTotalTx(data.total);
    } catch { /* ignore */ }
  }, [pageTx, searchTx]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadTx(); }, [loadTx]);

  const submitItem = async (e) => {
    e.preventDefault();
    try {
      await api.post("/gudang", formItem);
      toast.success("Item ditambahkan");
      setDialogItem(false);
      setFormItem({ nama: "", kategori: "pakan", satuan: "kg", stok: 0, harga_satuan: 0, stok_minimum: 0 });
      loadItems();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const submitTx = async (e) => {
    e.preventDefault();
    try {
      await api.post("/gudang/transaksi", formTx);
      toast.success("Transaksi dicatat");
      setDialogTx(false);
      setFormTx({ item_id: "", tipe: "masuk", jumlah: 0, harga_total: 0, keterangan: "", tanggal: "" });
      loadItems();
      loadTx();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(v || 0);

  return (
    <div data-testid="gudang-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Gudang</h1>
          <p className="text-sm text-slate-500 mt-1">Manajemen inventaris dan stok gudang</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ImportDialog onSuccess={() => { loadItems(); loadTx(); }} />
          <Dialog open={dialogItem} onOpenChange={setDialogItem}>
            <DialogTrigger asChild>
              <Button data-testid="add-gudang-item-btn" className="bg-[#B8860B] hover:bg-[#9A7209] text-white"><Plus size={18} className="mr-2" /> Item Baru</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>Tambah Item Gudang</DialogTitle></DialogHeader>
              <form onSubmit={submitItem} className="space-y-4 mt-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Nama</Label><Input data-testid="input-gudang-nama" value={formItem.nama} onChange={e => setFormItem({ ...formItem, nama: e.target.value })} required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Kategori</Label>
                    <Select value={formItem.kategori} onValueChange={v => setFormItem({ ...formItem, kategori: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{KATEGORI.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Satuan</Label><Input value={formItem.satuan} onChange={e => setFormItem({ ...formItem, satuan: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Stok Awal</Label><Input type="number" step="0.1" value={formItem.stok} onChange={e => setFormItem({ ...formItem, stok: parseFloat(e.target.value) || 0 })} /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Harga Satuan</Label><Input type="number" value={formItem.harga_satuan} onChange={e => setFormItem({ ...formItem, harga_satuan: parseFloat(e.target.value) || 0 })} /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Stok Min</Label><Input type="number" step="0.1" value={formItem.stok_minimum} onChange={e => setFormItem({ ...formItem, stok_minimum: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <Button type="submit" data-testid="submit-gudang-item-btn" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogTx} onOpenChange={setDialogTx}>
            <DialogTrigger asChild>
              <Button data-testid="add-gudang-tx-btn" variant="outline" className="border-[#008080] text-[#008080] hover:bg-[#008080]/5"><Plus size={18} className="mr-2" /> Transaksi</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>Catat Transaksi Gudang</DialogTitle></DialogHeader>
              <form onSubmit={submitTx} className="space-y-4 mt-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Item</Label>
                  <Select value={formTx.item_id} onValueChange={v => setFormTx({ ...formTx, item_id: v })}><SelectTrigger data-testid="select-gudang-item"><SelectValue placeholder="Pilih item" /></SelectTrigger><SelectContent>{items.map(i => <SelectItem key={i._id} value={i._id}>{i.nama}</SelectItem>)}</SelectContent></Select></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tipe</Label>
                    <Select value={formTx.tipe} onValueChange={v => setFormTx({ ...formTx, tipe: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="masuk">Masuk</SelectItem><SelectItem value="keluar">Keluar</SelectItem></SelectContent></Select></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Jumlah</Label><Input type="number" step="0.1" value={formTx.jumlah} onChange={e => setFormTx({ ...formTx, jumlah: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Harga Total</Label><Input type="number" value={formTx.harga_total} onChange={e => setFormTx({ ...formTx, harga_total: parseFloat(e.target.value) || 0 })} /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal</Label><Input type="date" value={formTx.tanggal} onChange={e => setFormTx({ ...formTx, tanggal: e.target.value })} required /></div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Keterangan</Label><Input value={formTx.keterangan} onChange={e => setFormTx({ ...formTx, keterangan: e.target.value })} /></div>
                <Button type="submit" data-testid="submit-gudang-tx-btn" className="w-full bg-[#008080] hover:bg-[#006B6B] text-white">Catat</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="stok" className="w-full">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="stok" data-testid="tab-stok">Stok Gudang</TabsTrigger>
          <TabsTrigger value="transaksi" data-testid="tab-transaksi">Riwayat Transaksi</TabsTrigger>
        </TabsList>
        <TabsContent value="stok" className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-64">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cari item..." value={searchItems} onChange={e => { setSearchItems(e.target.value); setPageItems(1); }} className="pl-9 h-9" />
            </div>
            <ExportButtons onExcel={() => exportToExcel(items, STOK_COLS, "stok_gudang")} onPDF={() => exportToPDF(items, STOK_COLS, "stok_gudang", "Stok Gudang P3UW")} />
          </div>
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Nama</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Kategori</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Stok</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Satuan</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Harga Satuan</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Nilai</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Belum ada item</TableCell></TableRow>
                  ) : items.map(i => (
                    <TableRow key={i._id} className="hover:bg-slate-50">
                      <TableCell className="text-sm font-medium">{i.nama}</TableCell>
                      <TableCell><Badge variant="secondary">{i.kategori}</Badge></TableCell>
                      <TableCell className="text-sm font-semibold">{i.stok}</TableCell>
                      <TableCell className="text-sm">{i.satuan}</TableCell>
                      <TableCell className="text-sm">{formatCurrency(i.harga_satuan)}</TableCell>
                      <TableCell className="text-sm font-medium">{formatCurrency(i.stok * i.harga_satuan)}</TableCell>
                      <TableCell>
                        {i.stok <= (i.stok_minimum || 0) ? (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><Warning size={12} className="mr-1" />Rendah</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Cukup</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="px-4 pb-3"><Pagination page={pageItems} limit={15} total={totalItems} onPageChange={setPageItems} /></div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="transaksi" className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-64">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cari transaksi..." value={searchTx} onChange={e => { setSearchTx(e.target.value); setPageTx(1); }} className="pl-9 h-9" />
            </div>
            <ExportButtons onExcel={() => exportToExcel(transaksi, TX_COLS, "transaksi_gudang")} onPDF={() => exportToPDF(transaksi, TX_COLS, "transaksi_gudang", "Transaksi Gudang P3UW")} />
          </div>
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Tanggal</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Item</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Tipe</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Jumlah</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Harga</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Keterangan</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {transaksi.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Belum ada transaksi</TableCell></TableRow>
                  ) : transaksi.map(t => (
                    <TableRow key={t._id} className="hover:bg-slate-50">
                      <TableCell className="text-sm">{t.tanggal}</TableCell>
                      <TableCell className="text-sm font-medium">{t.item_nama}</TableCell>
                      <TableCell>
                        <Badge className={t.tipe === "masuk" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>
                          {t.tipe === "masuk" ? <><ArrowDown size={12} className="mr-1" />Masuk</> : <><ArrowUp size={12} className="mr-1" />Keluar</>}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-semibold">{t.jumlah}</TableCell>
                      <TableCell className="text-sm">{formatCurrency(t.harga_total)}</TableCell>
                      <TableCell className="text-sm text-slate-500">{t.keterangan || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="px-4 pb-3"><Pagination page={pageTx} limit={15} total={totalTx} onPageChange={setPageTx} /></div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
